import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import * as OTPAuth from 'otpauth';
import { doc, setDoc } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import '../styles/twofa.css';

function TwoFactorSetup({ onComplete }) {
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [totp, setTotp] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    generateSecret();
  }, []);

  const generateSecret = () => {
    const user = auth.currentUser;
    
    // Create TOTP instance
    const totpInstance = new OTPAuth.TOTP({
      issuer: 'StoreManager',
      label: user.email,
      algorithm: 'SHA1',
      digits: 6,
      period: 30,
      secret: OTPAuth.Secret.fromBase32(generateBase32Secret())
    });
    
    setTotp(totpInstance);
    setSecret(totpInstance.secret.base32);
    
    // Generate QR code
    const otpauthURL = totpInstance.toString();
    
    QRCode.toDataURL(otpauthURL)
      .then(url => setQrCode(url))
      .catch(err => console.error('QR Code error:', err));
  };

  const generateBase32Secret = () => {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
    let secret = '';
    for (let i = 0; i < 32; i++) {
      secret += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return secret;
  };

  const handleVerify = async () => {
    setError('');
    
    if (verifyCode.length !== 6) {
      setError('Please enter 6-digit code');
      return;
    }

    try {
      // Verify TOTP code
      const delta = totp.validate({ token: verifyCode, window: 1 });
      
      if (delta !== null) {
        // Code is valid, save to Firestore
        const user = auth.currentUser;
        await setDoc(doc(db, 'users', user.uid), {
          email: user.email,
          totpSecret: secret,
          totpEnabled: true
        });

        onComplete();
      } else {
        setError('Invalid code. Please try again.');
        setVerifyCode('');
      }
    } catch (err) {
      console.error('2FA setup error:', err);
      setError('Failed to enable 2FA. Please try again.');
    }
  };

  return (
    <div className="twofa-container">
      <div className="twofa-box">
        <h2 className="twofa-title">🔐 Two-Factor Authentication</h2>
        <p className="twofa-subtitle">Secure your account with TOTP</p>

        <div className="twofa-steps">
          <div className="twofa-step">
            <div className="step-number">1</div>
            <div className="step-text">Download Google Authenticator or Microsoft Authenticator app</div>
          </div>
          
          <div className="twofa-step">
            <div className="step-number">2</div>
            <div className="step-text">Scan this QR code with the app</div>
          </div>

          {qrCode && (
            <div className="qr-container">
              <img src={qrCode} alt="QR Code" className="qr-image" />
            </div>
          )}

          <div className="secret-container">
            <p className="secret-label">Or enter this code manually:</p>
            <div className="secret-code">{secret}</div>
          </div>

          <div className="twofa-step">
            <div className="step-number">3</div>
            <div className="step-text">Enter the 6-digit code from the app</div>
          </div>

          {error && <div className="twofa-error">{error}</div>}

          <input
            type="text"
            className="verify-input"
            placeholder="000000"
            maxLength="6"
            value={verifyCode}
            onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
          />

          <div className="twofa-actions">
            <button 
              className="btn-verify btn-verify-full" 
              onClick={handleVerify}
              disabled={verifyCode.length !== 6}
            >
              ✓ Enable 2FA
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TwoFactorSetup;